// src/modules/app-views/fiscalConfig/pages/Document/components/FiscalServiceTags.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Tag, Tooltip, Space } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import invoicesService from 'modules/app-views/invoices/service';
import fiscalService from 'services/fiscalService';
import { useFiscalState } from 'modules/app-views/fiscalConfig/pages/Document/context/FiscalStateContext';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * FiscalServiceTags - Renders fiscal service tags with real-time state
 * Uses FiscalStateContext to get switch state updates before saving
 * @param {object} data - Document data from backend (used for certificate check)
 * @param {boolean} showStatusIcons - Show check/X icons on service tags (default: false)
 */
const FiscalServiceTags = ({ data, showStatusIcons = false }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Get real-time fiscal enabled state from context
  const { fiscalEnabled } = useFiscalState();
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasCertificate = !!data?.fiscal?.certificate;
  const resources = storedSubscription?.resources || [];

  // Map fiscal type to required resource
  const RESOURCE_MAP = {
    nfe: 'moduleresources_nfe',
    nfce: 'moduleresources_nfce',
    nfse: 'moduleresources_nfse',
  };

  // Use context values (real-time) with fallback to data (saved values)
  // Only include fiscal types that the subscription has resources for
  const fiscalTypes = [
    {
      key: 'nfe',
      isEnabled: fiscalEnabled.nfe ?? data?.fiscalData?.nfe?.isEnabled ?? false,
    },
    {
      key: 'nfce',
      isEnabled: fiscalEnabled.nfce ?? data?.fiscalData?.nfce?.isEnabled ?? false,
    },
    {
      key: 'nfse',
      isEnabled: fiscalEnabled.nfse ?? data?.fiscalData?.nfse?.isEnabled ?? false,
    },
  ].filter(ft => resources.includes(RESOURCE_MAP[ft.key]));

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex gap="4px 0" wrap align="center">
      {/* Certificate Tag */}
      <Tooltip
        title={translateX(hasCertificate ? 'certificate_attached' : 'certificate_not_attached')}
      >
        <Tag
          color={hasCertificate ? 'green' : 'red'}
          variant="outlined"
          style={{ marginInlineEnd: 8, cursor: 'default' }}
        >
          <Space size={4}>
            {hasCertificate ? <CheckOutlined /> : <CloseOutlined />}
            <span style={{ color: hasCertificate ? 'green' : 'red' }}>
              {translateX('digital_certificate')}
            </span>
          </Space>
        </Tag>
      </Tooltip>

      {/* Fiscal Services Tags */}
      {fiscalTypes.map(({ key, isEnabled }) => {
        const enabledColor = invoicesService.getInvoiceTypeColor(key);
        const color = isEnabled ? enabledColor : 'red';
        const label = fiscalService.getDocumentTypeLabel(key);
        const tooltip = isEnabled ? 'service_enabled' : 'service_not_enabled';

        return (
          <Tooltip key={key} title={translateX(tooltip)}>
            <Tag color={color} variant="outlined" style={{ marginInlineEnd: 8, cursor: 'default' }}>
              {showStatusIcons ? (
                <Space size={4}>
                  {isEnabled ? <CheckOutlined /> : <CloseOutlined />}
                  <span style={{ color }}>{label}</span>
                </Space>
              ) : (
                <span style={isEnabled ? { color } : undefined}>{label}</span>
              )}
            </Tag>
          </Tooltip>
        );
      })}
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FiscalServiceTags;
